import axios from 'axios';

const BASE_URL = '/api/tmdb';

const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

const getCached = (key) => {
  const item = cache.get(key);
  if (!item) return null;
  if (Date.now() > item.expiry) {
    cache.delete(key);
    return null;
  }
  return item.data;
};

const setCache = (key, data) => {
  cache.set(key, { data, expiry: Date.now() + CACHE_TTL });
};

const tmdb = axios.create({ baseURL: BASE_URL });

const hasPoster = (item) => Boolean(item && item.poster_path);

export const getTrendingMovies = async (page = 1) => {
  const key = `trending_movies_${page}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(`/movie/popular?include_adult=false&page=${page}`);
  const results = (data.results || []).filter(hasPoster);
  setCache(key, results);
  return results;
};

export const getTrendingTvShows = async (page = 1) => {
  const key = `trending_tv_${page}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(`/trending/tv/day?include_adult=false&page=${page}`);
  const results = (data.results || []).filter(hasPoster);
  setCache(key, results);
  return results;
};

export const getMovieDetails = async (movieId) => {
  const key = `movie_${movieId}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(
    `/movie/${movieId}?include_adult=false&append_to_response=credits,videos`
  );
  setCache(key, data);
  return data;
};

export const getTvShowDetails = async (id) => {
  const key = `tv_${id}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(
    `/tv/${id}?include_adult=false&append_to_response=credits,videos`
  );

  if (!data || !data.seasons) {
    throw new Error('TV show details unavailable');
  }

  // Drop "Specials" (season 0) and any junk seasons without episodes.
  // Carry only the metadata we need; episodes are loaded lazily on demand.
  const seasons = data.seasons
    .filter((s) => s.season_number > 0 && (s.episode_count ?? 0) > 0)
    .map((s) => ({
      season_number: s.season_number,
      name: s.name,
      episode_count: s.episode_count,
      poster_path: s.poster_path,
      air_date: s.air_date,
      overview: s.overview,
    }));

  const tvShowDetails = {
    id: data.id,
    name: data.name,
    first_air_date: data.first_air_date,
    episode_run_time: data.episode_run_time,
    genres: data.genres,
    overview: data.overview,
    vote_average: data.vote_average,
    backdrop_path: data.backdrop_path,
    poster_path: data.poster_path,
    tagline: data.tagline,
    number_of_seasons: data.number_of_seasons,
    number_of_episodes: data.number_of_episodes,
    seasons,
    credits: data.credits,
  };

  setCache(key, tvShowDetails);
  return tvShowDetails;
};

export const getSeasonEpisodes = async (tvShowId, seasonNumber) => {
  const key = `tv_${tvShowId}_season_${seasonNumber}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(`/tv/${tvShowId}/season/${seasonNumber}`);

  const episodes = (data.episodes || []).map((ep) => ({
    id: ep.id,
    episode_number: ep.episode_number,
    name: ep.name,
    overview: ep.overview,
    still_path: ep.still_path,
    air_date: ep.air_date,
    runtime: ep.runtime,
    vote_average: ep.vote_average,
  }));

  setCache(key, episodes);
  return episodes;
};

export const searchMedia = async (query) => {
  try {
    const response = await fetch(
      `${BASE_URL}/search/multi?include_adult=false&query=${encodeURIComponent(query)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch search results');
    }

    const data = await response.json();

    return (data.results || []).map((r) => ({
      id: r.id,
      title: r.title || r.name,
      poster_path: r.poster_path ? r.poster_path : null,
      media_type: r.media_type,
    }));
  } catch (error) {
    console.error('Error fetching search results:', error.message);
    throw error;
  }
};

export const getMovieRecommendations = async (movieId) => {
  const key = `movie_rec_${movieId}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(`/movie/${movieId}/recommendations`);
  const results = (data.results || []).filter(hasPoster);
  setCache(key, results);
  return results;
};

export const getTvRecommendations = async (tvId) => {
  const key = `tv_rec_${tvId}`;
  const cached = getCached(key);
  if (cached) return cached;

  const { data } = await tmdb.get(`/tv/${tvId}/recommendations`);
  const results = (data.results || []).filter(hasPoster);
  setCache(key, results);
  return results;
};

const pickTrailerKey = (videos) => {
  if (!videos || !videos.results) return null;
  // Prefer official YouTube trailers
  const official = videos.results.find(
    (v) => v.type === 'Trailer' && v.site === 'YouTube' && v.official
  );
  if (official) return official.key;
  const trailer = videos.results.find(
    (v) => v.type === 'Trailer' && v.site === 'YouTube'
  );
  return trailer ? trailer.key : null;
};

export const getMovieTrailerKey = (details) => pickTrailerKey(details?.videos);
export const getTvTrailerKey = (details) => pickTrailerKey(details?.videos);
